import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { useClientUser } from '@/hooks';
import { useMutation } from '@apollo/client';
import {
    Checkbox,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
} from '@material-ui/core';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
    Body2,
    GridItemRow,
    ModalActions,
    ModalContent,
    ModalSubtitle,
    ModalTitle,
    PrimaryButton,
    SecondaryButton,
} from '../blocks';
import { MODALS } from '../constants';
import { PlanningContext } from '../context';
import {
    UPDATE_SETTINGS,
    UPDATE_CLIENT_DISPATCH_SETTINGS_DEFAULT,
    UPDATE_CLIENT_DISPATCH_SETTINGS_PARTNER,
    INSERT_CLIENT_DISPATCH_SETTINGS_DEFAULT,
    INSERT_CLIENT_DISPATCH_SETTINGS_PARTNER,
} from '../graphql/mutations';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { colors } from '@/styles';
import { OnwardCheckbox } from '@/components/ShipmentForm/blocks';
import { HelpOutline } from '@material-ui/icons';
import { hourMinuteFormatter, hourString, minuteStringLong } from '@/constants/formats';
import format from 'date-fns-tz/format';
import { withStyles } from '@material-ui/core/styles';
import { carrierDisplayName, sortByCarrierDisplayName } from '@/utilities/carrierAlias';

const TIMEFRAME_OPTIONS = [1, 2, 3, 4];
const ROUND_OPTIONS = [
    { value: 'none', label: 'Do Not Round' },
    { value: 15, label: 'Nearest 15th Minute' },
    { value: 30, label: 'Nearest 30th Minute' },
];
const BUFFER_OPTIONS = [30, 45, 60, 75, 90, 105, 120];
const SERVICE_TIME_OPTIONS = Array.from(Array(19).keys()).map((_, idx) => idx * 5);
const SERVICE_TIME_INCR_OPTIONS = [5, 10, 15];
const TOTAL_ROUTE_TIME_OPTIONS = Array.from(Array(32).keys()).map((_, idx) => ({
    hours: Math.floor((idx + 1) / 2),
    minutes: ((idx + 1) % 2) * 30,
}));

const ROUTE_EOD_TIME_OPTIONS = Array.from(Array(48).keys()).map((_, idx) => {
    const totalMins = idx * 30;
    const minutes = totalMins % 60;
    const hours = Math.floor(totalMins / 60);

    return {
        hours,
        minutes,
    };
});

const DELIVERY_WINDOW_FLOOR_OPTIONS = Array.from(Array(48).keys()).map((_, idx) => {
    const totalMins = idx * 30;
    const minutes = totalMins % 60;
    const hours = Math.floor(totalMins / 60);

    return {
        hours,
        minutes,
    };
});

const DEFAULT_START_TIME_OPTIONS = Array.from(Array(48).keys()).map((_, idx) => {
    const totalMins = idx * 30;
    const minutes = totalMins % 60;
    const hours = Math.floor(totalMins / 60);

    return {
        hours,
        minutes,
    };
});

const Row = styled((props) => <Grid container xs={12} {...props} />)`
    display: flex;
    padding: 0.5rem 0.5rem;
    align-items: center;
`;

const Item = styled((props) => <Grid item xs={6} {...props} />)`
    display: flex;
    align-items: center;
    padding: 0rem 0.5rem;
    width: 300px;
`;

const SettingsTitle = styled((props) => <ModalTitle {...props} />)`
    padding-bottom: 0;
    font-weight: 900;
    font-size: 18px;
    color: ${colors.greys.secondary};
`;

const SettingsContent = styled((props) => <ModalContent {...props} />)`
    padding: 0.5rem 0;
    .MuiFormControl-root {
        width: 100%;
    }
    .MuiInputLabel-formControl {
        margin-left: 0.5rem;
        margin-top: -0.25rem;
        background-color: white;
        z-index: 5;
        padding: 0 0.5rem;
        color: black;
    }
    .input[type='number'] {
        -moz-appearance: textfield;
    }
    .input[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
    }
    .input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
    }
    border-top: none;
    overflow-y: visible;
`;

const CustomTooltip = withStyles(() => ({
    tooltip: {
        fontSize: '14px !important',
        backgroundColor: 'rgba(97, 97, 97, 1.0) !important',
    },
}))(Tooltip);

const SettingsModal = () => {
    const {
        user_id,
        preferences_service_time,
        preferences_service_time_incr,
        preferences_service_time_curbside,
        preferences_service_time_first_dry_space,
        preferences_service_time_room_of_choice,
        preferences_service_time_white_glove,
        preferences_delivery_timeframe,
        preferences_timeframe_buffer,
        preferences_timeframe_buffer_apply,
        preferences_timeframe_round,
        preferences_timeframe_round_minute,
        preferences_delivery_window_floor,
        preferences_delivery_window_ceiling,
        preferences_default_start_time,
        preferences_route_time_limit,
        preferences_route_eod_limit,
        preferences_route_eod_limit_next_day,
        preferences_start_estimate,
        preferences_end_estimate,
        preferences_warehouse_return_estimate,
        preferences_use_service_default_as_minimum,
        preferences_require_staging_verification,
        preferences_next_day_returns,
        preferences_bypass_optimize,
        circles,
        partner_carriers,
        partnerCarriersById,
        dispatch_settings,
    } = useClientUser();
    const { modalOpen, setModalOpen, setNotification, setError } = useContext(PlanningContext);

    // The client query orders shipper_partnerships by carrier.business_name, which an
    // aliased shipper is never shown. Re-sort by the label this picker renders.
    const sortedPartnerCarriers = useMemo(
        () => sortByCarrierDisplayName(partner_carriers, partnerCarriersById, (partner) => partner.carrier),
        [partner_carriers, partnerCarriersById]
    );

    const [selectedShipper, setSelectedShipper] = useState(null);

    const [settings, setSettings] = useState({
        preferences_service_time,
        preferences_service_time_incr,
        preferences_service_time_curbside,
        preferences_service_time_first_dry_space,
        preferences_service_time_room_of_choice,
        preferences_service_time_white_glove,
        preferences_delivery_timeframe,
        preferences_timeframe_buffer,
        preferences_timeframe_buffer_apply,
        preferences_timeframe_round,
        preferences_timeframe_round_minute,
        preferences_delivery_window_floor,
        preferences_delivery_window_ceiling,
        preferences_default_start_time,
        preferences_route_time_limit,
        preferences_route_eod_limit,
        preferences_route_eod_limit_next_day,
        preferences_start_estimate,
        preferences_end_estimate,
        preferences_warehouse_return_estimate,
        preferences_use_service_default_as_minimum,
        preferences_require_staging_verification,
        preferences_next_day_returns,
        preferences_bypass_optimize,
    });

    useEffect(() => {
        if (modalOpen) {
            setSettings({
                preferences_service_time,
                preferences_service_time_incr,
                preferences_service_time_curbside,
                preferences_service_time_first_dry_space,
                preferences_service_time_room_of_choice,
                preferences_service_time_white_glove,
                preferences_delivery_timeframe,
                preferences_timeframe_buffer,
                preferences_timeframe_buffer_apply,
                preferences_timeframe_round,
                preferences_timeframe_round_minute,
                preferences_delivery_window_floor,
                preferences_delivery_window_ceiling,
                preferences_default_start_time,
                preferences_route_time_limit,
                preferences_route_eod_limit,
                preferences_route_eod_limit_next_day,
                preferences_start_estimate,
                preferences_end_estimate,
                preferences_warehouse_return_estimate,
                preferences_use_service_default_as_minimum,
                preferences_require_staging_verification,
                preferences_next_day_returns,
                preferences_bypass_optimize,
            });
        }
    }, [modalOpen]);

    // Load dispatch settings for the selected shipper (or the user's own defaults when none is selected).
    useEffect(() => {
        const dispatchSettingsForShipper = dispatch_settings?.[selectedShipper || 'DEFAULT'];
        if (!dispatchSettingsForShipper) return;

        setSettings((prev) => ({
            ...prev,
            preferences_delivery_timeframe:
                dispatchSettingsForShipper.preferences_delivery_timeframe ?? prev.preferences_delivery_timeframe,
            preferences_timeframe_buffer:
                dispatchSettingsForShipper.preferences_timeframe_buffer ?? prev.preferences_timeframe_buffer,
            preferences_delivery_window_floor:
                dispatchSettingsForShipper.preferences_delivery_window_floor ?? prev.preferences_delivery_window_floor,
            preferences_delivery_window_ceiling:
                dispatchSettingsForShipper.preferences_delivery_window_ceiling ??
                prev.preferences_delivery_window_ceiling,
            preferences_default_start_time:
                dispatchSettingsForShipper.preferences_default_start_time ?? prev.preferences_default_start_time,
            preferences_timeframe_round:
                dispatchSettingsForShipper.preferences_timeframe_round ?? prev.preferences_timeframe_round,
            preferences_timeframe_round_minute:
                dispatchSettingsForShipper.preferences_timeframe_round_minute ??
                prev.preferences_timeframe_round_minute,
            preferences_timeframe_buffer_apply:
                dispatchSettingsForShipper.preferences_timeframe_buffer_apply ??
                prev.preferences_timeframe_buffer_apply,
            preferences_service_time:
                dispatchSettingsForShipper.preferences_service_time ?? prev.preferences_service_time,
            preferences_service_time_curbside:
                dispatchSettingsForShipper.preferences_service_time_curbside ?? prev.preferences_service_time_curbside,
            preferences_service_time_room_of_choice:
                dispatchSettingsForShipper.preferences_service_time_room_of_choice ??
                prev.preferences_service_time_room_of_choice,
            preferences_service_time_incr:
                dispatchSettingsForShipper.preferences_service_time_incr ?? prev.preferences_service_time_incr,
            preferences_service_time_first_dry_space:
                dispatchSettingsForShipper.preferences_service_time_first_dry_space ??
                prev.preferences_service_time_first_dry_space,
            preferences_service_time_white_glove:
                dispatchSettingsForShipper.preferences_service_time_white_glove ??
                prev.preferences_service_time_white_glove,
            preferences_use_service_default_as_minimum:
                dispatchSettingsForShipper.preferences_use_service_default_as_minimum ??
                prev.preferences_use_service_default_as_minimum,
            preferences_require_staging_verification:
                dispatchSettingsForShipper.preferences_require_staging_verification ??
                prev.preferences_require_staging_verification,
        }));
    }, [selectedShipper, dispatch_settings]);

    const updateSettings = (key, val) => setSettings((prev) => ({ ...prev, [key]: val }));

    // Fields that go to client_dispatch_settings table
    const DISPATCH_SETTINGS_FIELDS = [
        'preferences_delivery_timeframe',
        'preferences_timeframe_buffer',
        'preferences_delivery_window_floor',
        'preferences_delivery_window_ceiling',
        'preferences_default_start_time',
        'preferences_timeframe_round',
        'preferences_timeframe_round_minute',
        'preferences_timeframe_buffer_apply',
        'preferences_service_time',
        'preferences_service_time_curbside',
        'preferences_service_time_room_of_choice',
        'preferences_service_time_incr',
        'preferences_service_time_first_dry_space',
        'preferences_service_time_white_glove',
        'preferences_use_service_default_as_minimum',
        'preferences_require_staging_verification',
    ];

    const [updateDispatchSettingsDefault] = useMutation(UPDATE_CLIENT_DISPATCH_SETTINGS_DEFAULT);
    const [insertDispatchSettingsDefault] = useMutation(INSERT_CLIENT_DISPATCH_SETTINGS_DEFAULT);
    const [updateDispatchSettingsPartner] = useMutation(UPDATE_CLIENT_DISPATCH_SETTINGS_PARTNER);
    const [insertDispatchSettingsPartner] = useMutation(INSERT_CLIENT_DISPATCH_SETTINGS_PARTNER);

    const [saveClientSettings, { loading: loadingClientSettings }] = useMutation(UPDATE_SETTINGS, {
        onCompleted: () => {
            setNotification({
                severity: 'success',
                message: 'Successfully updated delivery settings',
            });
            handleModalClose();
        },
        onError: (error) => {
            setError(error, 'Error saving delivery settings');
        },
    });

    const [isLoading, setIsLoading] = useState(false);

    const handleModalClose = () => {
        setModalOpen(null);
    };

    const handleSaveSettings = async () => {
        setIsLoading(true);
        try {
            // Split settings into dispatch settings and client settings
            const dispatchSettings = {};
            const clientSettings = {};

            Object.entries(settings).forEach(([key, value]) => {
                if (DISPATCH_SETTINGS_FIELDS.includes(key)) {
                    dispatchSettings[key] = value;
                } else {
                    clientSettings[key] = value;
                }
            });

            // Convert string-type fields from numbers to strings
            const convertedDispatchSettings = {
                ...dispatchSettings,
                preferences_delivery_window_floor: dispatchSettings.preferences_delivery_window_floor
                    ? dispatchSettings.preferences_delivery_window_floor
                    : null,
                preferences_delivery_window_ceiling: dispatchSettings.preferences_delivery_window_ceiling
                    ? dispatchSettings.preferences_delivery_window_ceiling
                    : null,
                preferences_default_start_time: dispatchSettings.preferences_default_start_time
                    ? dispatchSettings.preferences_default_start_time
                    : null,
            };

            // Try to update dispatch settings first
            let dispatchUpdateResult;
            if (selectedShipper) {
                dispatchUpdateResult = await updateDispatchSettingsPartner({
                    variables: {
                        client_id: user_id,
                        partner_client_id: selectedShipper,
                        ...convertedDispatchSettings,
                    },
                });
            } else {
                dispatchUpdateResult = await updateDispatchSettingsDefault({
                    variables: {
                        client_id: user_id,
                        ...convertedDispatchSettings,
                    },
                });
            }

            // If update affected 0 rows, the record doesn't exist, so insert it
            if (dispatchUpdateResult.data?.update_client_dispatch_settings?.affected_rows === 0) {
                if (selectedShipper) {
                    await insertDispatchSettingsPartner({
                        variables: {
                            client_id: user_id,
                            partner_client_id: selectedShipper,
                            ...convertedDispatchSettings,
                        },
                    });
                } else {
                    await insertDispatchSettingsDefault({
                        variables: {
                            client_id: user_id,
                            ...convertedDispatchSettings,
                        },
                    });
                }
            }

            // Prepare client settings with null values for optional fields
            const clientSettingsUpdate = {
                ...clientSettings,
                preferences_route_time_limit: clientSettings.preferences_route_time_limit
                    ? clientSettings.preferences_route_time_limit
                    : null,
                preferences_route_eod_limit: clientSettings.preferences_route_eod_limit
                    ? clientSettings.preferences_route_eod_limit
                    : null,
            };

            // Save client settings (this has onCompleted handler that shows success and closes modal)
            await saveClientSettings({
                variables: {
                    client_id: user_id,
                    update: clientSettingsUpdate,
                },
            });
        } catch (error) {
            setError(error, 'Error saving delivery settings');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <NavResponsiveModal maxWidth="lg" open={modalOpen === MODALS.SETTINGS} onClose={handleModalClose}>
            {circles?.['asset-heavy'] && partner_carriers?.length > 0 && (
                <SettingsContent>
                    <Row>
                        <Item>
                            <FormControl>
                                <InputLabel shrink id="settings-shipper-label">
                                    Manage Settings For
                                </InputLabel>
                                <Select
                                    labelId="settings-shipper-label"
                                    id="settings-shipper"
                                    variant="outlined"
                                    value={selectedShipper || 'default'}
                                    onChange={(e) => setSelectedShipper(e.target.value || null)}
                                    renderValue={(value) => {
                                        if (value === 'default') {
                                            return 'Default Settings';
                                        }
                                        // `value` is the carrier's client_id, which is the map's key.
                                        return (
                                            carrierDisplayName(
                                                partnerCarriersById?.[value]?.carrier,
                                                partnerCarriersById
                                            ) || ''
                                        );
                                    }}
                                >
                                    <MenuItem key="default" value="">
                                        Default Settings
                                    </MenuItem>
                                    {sortedPartnerCarriers.map((partner) => (
                                        <MenuItem key={partner.carrier.client_id} value={partner.carrier.client_id}>
                                            {carrierDisplayName(partner.carrier, partnerCarriersById)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Item>
                    </Row>
                </SettingsContent>
            )}
            <SettingsTitle>Delivery Time Frame Settings</SettingsTitle>
            <ModalSubtitle>
                Set default time frame you'd like to give your customers as to when you will arrive with delivery.
            </ModalSubtitle>
            <SettingsContent>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-timeframe-label">
                                Arrival Time Frame
                            </InputLabel>
                            <Select
                                labelId="settings-timeframe-label"
                                id="settings-timeframe"
                                variant="outlined"
                                label="Arrival Time Frame"
                                value={settings.preferences_delivery_timeframe}
                                onChange={(e) =>
                                    updateSettings('preferences_delivery_timeframe', parseInt(e.target.value))
                                }
                            >
                                {TIMEFRAME_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {hourString.format(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-timeframe-round-label">
                                Round Arrival Time Frame
                            </InputLabel>
                            <Select
                                labelId="settings-timeframe-round-label"
                                id="settings-timeframe-round"
                                variant="outlined"
                                label="Round Arrival Time Frame"
                                value={
                                    settings.preferences_timeframe_round
                                        ? settings.preferences_timeframe_round_minute || 30
                                        : 'none'
                                }
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === 'none') {
                                        updateSettings('preferences_timeframe_round', false);
                                        updateSettings('preferences_timeframe_round_minute', null);
                                    } else {
                                        updateSettings('preferences_timeframe_round', true);
                                        updateSettings('preferences_timeframe_round_minute', val);
                                    }
                                }}
                            >
                                {ROUND_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <CustomTooltip
                            title="The arrival time frame will be rounded to the nearest 15 or 30 minutes. If a buffer is added this will always round down to ensure the minimum buffer amount is added."
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item>
                </Row>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-buffer-label">
                                Arrival Buffer
                            </InputLabel>
                            <Select
                                labelId="settings-buffer-label"
                                id="settings-buffer"
                                variant="outlined"
                                value={settings.preferences_timeframe_buffer}
                                onChange={(e) =>
                                    updateSettings('preferences_timeframe_buffer', parseInt(e.target.value))
                                }
                            >
                                {BUFFER_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {hourMinuteFormatter.format(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <OnwardCheckbox
                            label="Buffer arrival time frame"
                            checked={settings.preferences_timeframe_buffer_apply}
                            onChange={(e) =>
                                updateSettings('preferences_timeframe_buffer_apply', Boolean(e.target.checked))
                            }
                        />
                        <CustomTooltip
                            title="The arrival time frame will be increased by this amount. Expect the driver to arrive this far into the time frame."
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item>
                </Row>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-delivery_window_floor-label">
                                Delivery Window Floor
                            </InputLabel>
                            <Select
                                labelId="settings-delivery_window_floor-label"
                                id="settings-delivery_window_floor"
                                variant="outlined"
                                value={settings.preferences_delivery_window_floor || 0}
                                onChange={(e) => {
                                    updateSettings('preferences_delivery_window_floor', e.target.value);
                                }}
                                renderValue={(value) => {
                                    if (value && (value.hours || value.minutes)) {
                                        let _date = new Date();
                                        _date.setHours(value.hours);
                                        _date.setMinutes(value.minutes);
                                        const text = format(_date, 'h:mm aa');
                                        return text;
                                    }
                                    return 'None';
                                }}
                            >
                                <MenuItem key={'None'} value={0}>
                                    None
                                </MenuItem>
                                {DELIVERY_WINDOW_FLOOR_OPTIONS.map((option) => {
                                    let _date = new Date();
                                    _date.setHours(option.hours);
                                    _date.setMinutes(option.minutes);
                                    const text = format(_date, 'h:mm aa');
                                    return (
                                        <MenuItem key={`${option.hours}-${option.minutes}`} value={option}>
                                            {text}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <CustomTooltip
                            title="The earliest time that delivery windows can be scheduled. For example, if set to 8:00 AM, deliveries cannot have a start time earlier than 8:00 AM in the customer's local timezone."
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item>
                </Row>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-delivery_window_ceiling-label">
                                Delivery Window Ceiling
                            </InputLabel>
                            <Select
                                labelId="settings-delivery_window_ceiling-label"
                                id="settings-delivery_window_ceiling"
                                variant="outlined"
                                value={settings.preferences_delivery_window_ceiling || 0}
                                onChange={(e) => {
                                    updateSettings('preferences_delivery_window_ceiling', e.target.value);
                                }}
                                renderValue={(value) => {
                                    if (value && (value.hours || value.minutes)) {
                                        let _date = new Date();
                                        _date.setHours(value.hours);
                                        _date.setMinutes(value.minutes);
                                        const text = format(_date, 'h:mm aa');
                                        return text;
                                    }
                                    return 'None';
                                }}
                            >
                                <MenuItem key={'None'} value={0}>
                                    None
                                </MenuItem>
                                {DELIVERY_WINDOW_FLOOR_OPTIONS.map((option) => {
                                    let _date = new Date();
                                    _date.setHours(option.hours);
                                    _date.setMinutes(option.minutes);
                                    const text = format(_date, 'h:mm aa');
                                    return (
                                        <MenuItem key={`${option.hours}-${option.minutes}`} value={option}>
                                            {text}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <CustomTooltip
                            title="The latest time that a delivery window can end. For example, if set to 5:00 PM with a 4-hour arrival time frame, the last window will be 1:00 PM - 5:00 PM. Stops whose window would end later are not blocked, but they will be flagged with a warning."
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item>
                </Row>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-default_start_time-label">
                                Default Start Time
                            </InputLabel>
                            <Select
                                labelId="settings-default_start_time-label"
                                id="settings-default_start_time"
                                variant="outlined"
                                value={settings.preferences_default_start_time || 0}
                                onChange={(e) => {
                                    updateSettings('preferences_default_start_time', e.target.value);
                                }}
                                renderValue={(value) => {
                                    if (value && (value.hours || value.minutes)) {
                                        let _date = new Date();
                                        _date.setHours(value.hours);
                                        _date.setMinutes(value.minutes);
                                        return format(_date, 'h:mm aa');
                                    }
                                    return 'Default (7:00 AM)';
                                }}
                            >
                                <MenuItem key={'None'} value={0}>
                                    Default (7:00 AM)
                                </MenuItem>
                                {DEFAULT_START_TIME_OPTIONS.map((option) => {
                                    let _date = new Date();
                                    _date.setHours(option.hours);
                                    _date.setMinutes(option.minutes);
                                    const text = format(_date, 'h:mm aa');
                                    return (
                                        <MenuItem key={`${option.hours}-${option.minutes}`} value={option}>
                                            {text}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <CustomTooltip
                            title="The default Pickup Arrival Time for your routes. Individual routes can still be adjusted in Route Details. If left as Default, routes start at 7:00 AM."
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item>
                </Row>
            </SettingsContent>
            <SettingsTitle>Delivery Duration Settings</SettingsTitle>
            <ModalSubtitle>Customize the amount of time per delivery.</ModalSubtitle>
            <SettingsContent>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-service_time-label">
                                Default Service Time
                            </InputLabel>
                            <Select
                                labelId="settings-service_time-label"
                                id="settings-service_time"
                                variant="outlined"
                                value={settings.preferences_service_time}
                                onChange={(e) => updateSettings('preferences_service_time', parseInt(e.target.value))}
                            >
                                {SERVICE_TIME_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {minuteStringLong.format(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-service_time_incr-label">
                                Service Time Increment
                            </InputLabel>
                            <Select
                                labelId="settings-service_time_incr-label"
                                id="settings-service_time_incr"
                                variant="outlined"
                                value={settings.preferences_service_time_incr}
                                onChange={(e) =>
                                    updateSettings('preferences_service_time_incr', parseInt(e.target.value))
                                }
                            >
                                {SERVICE_TIME_INCR_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {minuteStringLong.format(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                </Row>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-service_time_curbside-label">
                                Curbside
                            </InputLabel>
                            <Select
                                labelId="settings-service_time_curbside-label"
                                id="settings-service_time_curbside"
                                variant="outlined"
                                value={settings.preferences_service_time_curbside}
                                onChange={(e) =>
                                    updateSettings('preferences_service_time_curbside', parseInt(e.target.value))
                                }
                            >
                                {SERVICE_TIME_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {minuteStringLong.format(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-service_time_first_dry_space-label">
                                First Dry Space
                            </InputLabel>
                            <Select
                                labelId="settings-service_time_first_dry_space-label"
                                id="settings-service_time_first_dry_space"
                                variant="outlined"
                                value={settings.preferences_service_time_first_dry_space}
                                onChange={(e) =>
                                    updateSettings('preferences_service_time_first_dry_space', parseInt(e.target.value))
                                }
                            >
                                {SERVICE_TIME_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {minuteStringLong.format(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                </Row>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-service_time_room_of_choice-label">
                                Room of Choice
                            </InputLabel>
                            <Select
                                labelId="settings-service_time_room_of_choice-label"
                                id="settings-service_time_room_of_choice"
                                variant="outlined"
                                value={settings.preferences_service_time_room_of_choice}
                                onChange={(e) =>
                                    updateSettings('preferences_service_time_room_of_choice', parseInt(e.target.value))
                                }
                            >
                                {SERVICE_TIME_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {minuteStringLong.format(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-service_time_white_glove-label">
                                White Glove
                            </InputLabel>
                            <Select
                                labelId="settings-service_time_white_glove-label"
                                id="settings-service_time_white_glove"
                                variant="outlined"
                                value={settings.preferences_service_time_white_glove}
                                onChange={(e) =>
                                    updateSettings('preferences_service_time_white_glove', parseInt(e.target.value))
                                }
                            >
                                {SERVICE_TIME_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {minuteStringLong.format(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                </Row>
                <Item
                    css={css`
                        width: 400px;
                    `}
                >
                    <OnwardCheckbox
                        label="Use defaults as minimums"
                        checked={settings.preferences_use_service_default_as_minimum}
                        onChange={(e) =>
                            updateSettings('preferences_use_service_default_as_minimum', Boolean(e.target.checked))
                        }
                    />
                    <CustomTooltip
                        title="If checked, this setting will use the above service times as the minimum service time for each respective service type."
                        placement="top"
                    >
                        <HelpOutline
                            css={css`
                                color: ${colors.greens.primary};
                                margin-left: 1rem;
                            `}
                        />
                    </CustomTooltip>
                </Item>
                <Item
                    css={css`
                        width: 400px;
                    `}
                >
                    <OnwardCheckbox
                        label="Require staging verification by default"
                        checked={settings.preferences_require_staging_verification}
                        onChange={(e) =>
                            updateSettings('preferences_require_staging_verification', Boolean(e.target.checked))
                        }
                    />
                </Item>
            </SettingsContent>
            <SettingsTitle>Route Time Limit Settings</SettingsTitle>
            <ModalSubtitle>
                Customize time limit constraints for your routes, before a warning appears letting you know that a route
                has gone over.
            </ModalSubtitle>
            <SettingsContent>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-total_route_time-label">
                                Total Route Time Limit
                            </InputLabel>
                            <Select
                                labelId="settings-total_route_time-label"
                                id="settings-total_route_time"
                                variant="outlined"
                                value={settings.preferences_route_time_limit}
                                onChange={(e) => updateSettings('preferences_route_time_limit', e.target.value)}
                                renderValue={(value) => {
                                    if (value) {
                                        let text = '';
                                        if (value.hours >= 1) {
                                            text += `${hourString.format(value.hours)}`;
                                        }
                                        if (value.minutes) {
                                            text += ` ${minuteStringLong.format(value.minutes)}`;
                                        }
                                        return text;
                                    }
                                    return 'None';
                                }}
                            >
                                <MenuItem key={'None'} value={0}>
                                    None
                                </MenuItem>
                                {TOTAL_ROUTE_TIME_OPTIONS.map((option) => {
                                    let text = '';
                                    if (option.hours >= 1) {
                                        text += `${hourString.format(option.hours)}`;
                                    }
                                    if (option.minutes) {
                                        text += ` ${minuteStringLong.format(option.minutes)}`;
                                    }
                                    return (
                                        <MenuItem key={`${option.hours}-${option.minutes}`} value={option}>
                                            {text}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Item>
                </Row>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel shrink id="settings-route_eod_limit-label">
                                Route End of Day Limit
                            </InputLabel>
                            <Select
                                labelId="settings-route_eod_limit-label"
                                id="settings-route_eod_limit"
                                variant="outlined"
                                value={settings.preferences_route_eod_limit}
                                onChange={(e) => {
                                    updateSettings('preferences_route_eod_limit', e.target.value);
                                }}
                                renderValue={(value) => {
                                    if (value) {
                                        let _date = new Date();
                                        _date.setHours(value.hours);
                                        _date.setMinutes(value.minutes);
                                        const text = format(_date, 'h:mm aa');
                                        return text;
                                    }
                                    return 'None';
                                }}
                            >
                                <MenuItem key={'None'} value={0}>
                                    None
                                </MenuItem>
                                {ROUTE_EOD_TIME_OPTIONS.map((option) => {
                                    let _date = new Date();
                                    _date.setHours(option.hours);
                                    _date.setMinutes(option.minutes);
                                    const text = format(_date, 'h:mm aa');
                                    return (
                                        <MenuItem key={`${option.hours}-${option.minutes}`} value={option}>
                                            {text}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item>
                        <OnwardCheckbox
                            label="Next day"
                            checked={settings.preferences_route_eod_limit_next_day}
                            onChange={(e) =>
                                updateSettings('preferences_route_eod_limit_next_day', Boolean(e.target.checked))
                            }
                        />
                        <CustomTooltip
                            title="If the end of day cutoff is at or after midnight, then this box should be checked."
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item>
                </Row>
            </SettingsContent>

            {/* Route Estimate Settings */}
            <SettingsTitle>Route Estimate Settings</SettingsTitle>
            <ModalSubtitle>
                Customize which stop types are factored into or skipped for route estimates, such as total route
                distance and driving time. Checked means that the stop type IS considered for route estimate
                calculations.
            </ModalSubtitle>
            <SettingsContent>
                <Row>
                    <Item>
                        <OnwardCheckbox
                            label="Route Start Location"
                            checked={settings.preferences_start_estimate}
                            onChange={(e) => updateSettings('preferences_start_estimate', Boolean(e.target.checked))}
                        />
                        {circles?.['overnight-routes'] && (
                            <CustomTooltip
                                title={
                                    'Note: If a route contains stops that are marked as Overnight, then the start location WILL be used as part of route total estimates, regardless of this setting.'
                                }
                                placement="top"
                            >
                                <HelpOutline
                                    css={css`
                                        color: ${colors.greens.primary};
                                        margin-left: 1rem;
                                    `}
                                />
                            </CustomTooltip>
                        )}
                    </Item>
                </Row>
                <Row>
                    {/* This setting only affects the Map Route button now, not sure if we should keep it or not. Might be misleading. */}
                    {/* <Item>
                        <OnwardCheckbox
                            label="Final Warehouse Dropoffs for Returns"
                            checked={settings.preferences_warehouse_return_estimate}
                            onChange={(e) => {
                                updateSettings('preferences_warehouse_return_estimate', Boolean(e.target.checked));
                                if (!e.target.checked) {
                                    updateSettings('preferences_end_estimate', Boolean(false));
                                }
                            }}
                        />
                        <CustomTooltip
                            title={
                                'After a return order has been picked up from a customer, a stop is added to the route to drop the items off back at the warehouse. Check this for this stop to be considered for route estimate calculations. Note: if this is disabled, route end location cannot be enabled for estimates.'
                            }
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item> */}
                    <Item>
                        <OnwardCheckbox
                            label="Route End Location"
                            checked={settings.preferences_end_estimate}
                            onChange={(e) => {
                                updateSettings('preferences_end_estimate', Boolean(e.target.checked));
                                if (e.target.checked) {
                                    updateSettings('preferences_warehouse_return_estimate', Boolean(true));
                                }
                            }}
                        />
                        <CustomTooltip
                            title="Check this for custom route end locations to be considered for route estimate calculations. Note: if this is enabled, final warehouse dropoffs must be enabled for estimates."
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item>
                </Row>
            </SettingsContent>

            {/* Return Settings */}
            <SettingsTitle>Returns</SettingsTitle>
            <ModalSubtitle>Settings regarding return orders</ModalSubtitle>
            <SettingsContent>
                <Row>
                    <Item>
                        <FormControl>
                            <InputLabel id="settings-preferences_next_day_returns-label">
                                Return to Warehouse
                            </InputLabel>
                            <Select
                                labelId="settings-preferences_next_day_returns-label"
                                id="settings-preferences_next_day_returns"
                                variant="outlined"
                                value={settings.preferences_next_day_returns || false}
                                onChange={(e) => updateSettings('preferences_next_day_returns', e.target.value)}
                            >
                                <MenuItem key={'same_day'} value={false}>
                                    Same Day
                                </MenuItem>
                                <MenuItem key={'next_day'} value={true}>
                                    Next Day
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Item>
                </Row>
            </SettingsContent>

            {/* Advanced Settings */}
            <SettingsTitle>Advanced Settings</SettingsTitle>
            <ModalSubtitle>Custom or experimental settings for advanced users</ModalSubtitle>
            <SettingsContent>
                <Row>
                    <Item>
                        <OnwardCheckbox
                            label="Bypass Optimization Requirements"
                            checked={settings.preferences_bypass_optimize}
                            onChange={(e) => {
                                updateSettings('preferences_bypass_optimize', Boolean(e.target.checked));
                            }}
                        />
                        <CustomTooltip
                            title={`Mapping or Optimizing a route after performing certain actions such as removing a stop will not be required. This will disable sending routes to Onward as it could potentially create poorly optimized routes with inaccurate timeframes.`}
                            placement="top"
                        >
                            <HelpOutline
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 1rem;
                                `}
                            />
                        </CustomTooltip>
                    </Item>
                </Row>
            </SettingsContent>

            <ModalActions>
                <SecondaryButton onClick={handleModalClose}>Cancel</SecondaryButton>
                <PrimaryButton onClick={handleSaveSettings} disabled={isLoading || loadingClientSettings}>
                    Save
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default SettingsModal;
